import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import Link from '@atlaskit/link';
import Button from '@atlaskit/button/new';
import SectionMessage from '@atlaskit/section-message';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import { useAiBulkNavigationGotoPage } from '../../providers/AiBulkNavigationContextProvider';
import { useBulkCreateContext, useGetIssueType } from '../../providers/BulkCreateContextProvider';
import { messages } from '../../messages';
import { BulkCreateErrorSectionMessage } from '../bulk-work-create-common/BulkCreateErrorSectionMessage';
import { useBulkCreateLoggingEvents } from '../../utils/useBulkCreateLoggingEvents';
import { useGetUnmappedRequiredFields } from '../../services/useGetUnmappedRequiredFields';
import { useIsAIIssueCreationAllowed } from '../../utils/useIsAIIssueCreationAllowed';
import {
	useAiIssueCreationEnablementState,
	useIssueCreateSidePanelContext,
} from '../../providers/IssueCreateSidePanelContextProvider';
import type { FieldMetadata } from '../../__types__/apiUtils';
import { IssueCreateWithAiToggle } from '../ai-work-create/IssueCreateWithAiToggle';
import { IssueCreateAiLoading } from '../ai-work-create/IssueCreateAiLoading';
import { getIsCreateIssueWithAiEnabledByUser } from '../../utils/localStorageHelpers';

import { BulkCreateIssueCard } from './BulkCreateIssueCard';

const bodyStyles = xcss({
	flexGrow: 1,
	overflowY: 'auto',
});

// Used for both AI and non-AI bulk create
export const BulkCreateBody = () => {
	const goToPage = useAiBulkNavigationGotoPage();

	const [
		{
			bulkIssuesList,
			currentJiraSite,
			currentJiraProject,
			isCreatedWithAi,
			isCreatedFromTable,
			isCreatedFromActionItems,
			aiResultViewedFiredOnce,
			aiState,
		},
		{ setAiResultViewedFiredOnce, setIsCreatedWithAi, setAiState },
	] = useBulkCreateContext();

	const [{ pageMode, hasEditPermission, contentId }] = useIssueCreateSidePanelContext();

	const { trackAIInteractionInit } = useAIEventsInstrumentation();
	const { logAiResultViewed } = useBulkCreateLoggingEvents();
	const unmappedRequiredFields: FieldMetadata[] = useGetUnmappedRequiredFields();
	const hasUnmappedRequiredFields = unmappedRequiredFields.length > 0;
	const [showCreateWithAiToggle, setShowCreateWithAiToggle] = useState(false);
	const aiIssueCreationEnablement = useAiIssueCreationEnablementState();
	const isAiLoading = aiState === 'loading';
	const issueType = useGetIssueType();

	useIsAIIssueCreationAllowed({ contentId, isBulkCreate: true });

	useEffect(() => {
		/*
		 * When ConfluenceAI=OFF & JiraAI=ON this component will manually create the issue first until
		 * selected site & project info is available to check Jira's AI enablement.  If JiraAI=ON then
		 * we can setIsCreatedWithAi to what the user's personal AI enablement to trigger a rerender.
		 */
		if (
			aiIssueCreationEnablement?.isEnabled &&
			(isCreatedFromActionItems || isCreatedFromTable) &&
			fg('confluence-create-work-items-from-action-items')
		) {
			setShowCreateWithAiToggle(true);
			void setIsCreatedWithAi(getIsCreateIssueWithAiEnabledByUser());
		} else {
			setShowCreateWithAiToggle(false);
			void setIsCreatedWithAi(false);
		}
	}, [
		isCreatedWithAi,
		isCreatedFromActionItems,
		isCreatedFromTable,
		aiIssueCreationEnablement?.isEnabled,
		setIsCreatedWithAi,
	]);

	const onConfigureClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
			event.preventDefault();
			void goToPage('configure');
		},
		[goToPage],
	);

	useEffect(() => {
		// TODO: Change this before rolling out confluence_ai_bulk_issue_creation_handle_chunks.
		if (isCreatedWithAi && bulkIssuesList.length > 0 && !aiResultViewedFiredOnce) {
			logAiResultViewed(bulkIssuesList.length);
			void setAiResultViewedFiredOnce(true);
		}
	}, [
		isCreatedWithAi,
		bulkIssuesList,
		logAiResultViewed,
		aiResultViewedFiredOnce,
		setAiResultViewedFiredOnce,
	]);

	const handleCreateManually = useCallback(() => {
		void setIsCreatedWithAi(false);
		void setAiState('idle');
		setShowCreateWithAiToggle(false);
	}, [setIsCreatedWithAi, setAiState]);

	const handleCreateWithAi = useCallback(() => {
		void setIsCreatedWithAi(true);
		void setAiState('loading');
		setShowCreateWithAiToggle(false);
	}, [setIsCreatedWithAi, setAiState]);

	const onAiToggleChange = useCallback(
		(enabled: boolean) => {
			if (enabled) {
				trackAIInteractionInit({
					attributes: {
						pageMode,
						hasEditPermission,
						aiEnablement: aiIssueCreationEnablement?.enablement,
					},
				});
				handleCreateWithAi();
			} else {
				handleCreateManually();
			}
		},
		[
			handleCreateWithAi,
			handleCreateManually,
			pageMode,
			hasEditPermission,
			aiIssueCreationEnablement?.enablement,
			trackAIInteractionInit,
		],
	);

	return (
		<Box padding="space.200" paddingBlockStart="space.100" xcss={bodyStyles}>
			<Stack space="space.200">
				{!isAiLoading && showCreateWithAiToggle && (
					<IssueCreateWithAiToggle onChange={onAiToggleChange} />
				)}
				{isAiLoading && <IssueCreateAiLoading loadingType="bulk" />}

				<Stack space="space.100">
					<BulkCreateErrorSectionMessage />
					{currentJiraSite && (
						<Inline space="space.050">
							<Text color="color.text.subtlest" weight="semibold">
								<FormattedMessage {...messages.jiraSiteHeading} />
							</Text>
							<Text color="color.text.subtlest">{currentJiraSite.displayName}</Text>
						</Inline>
					)}
					{currentJiraProject && (
						<Inline space="space.050">
							<Text color="color.text.subtlest" weight="semibold">
								<FormattedMessage
									{...(expVal('project-terminology-refresh', 'isEnabled', false)
										? messages.jiraProjectHeadingGalaxia
										: messages.jiraProjectHeading)}
								/>
							</Text>
							<Text color="color.text.subtlest">{currentJiraProject.name}</Text>
						</Inline>
					)}
					{fg('enghealth-39981_fix_issue_create_lint_suppression') ? (
						<Box>
							<Button
								spacing="compact"
								testId="bulk-create-configure-button"
								onClick={onConfigureClick}
							>
								<FormattedMessage {...messages.bulkCreateConfigureButtonText} />
							</Button>
						</Box>
					) : (
						<Text size="small" weight="bold">
							{/* eslint-disable-next-line @atlassian/a11y/anchor-is-valid, @atlassian/i18n/no-literal-string-in-jsx -- This is a valid use of <Link> as it is used for navigation */}
							<Link href="#" testId="bulk-create-configure-button" onClick={onConfigureClick}>
								Configure
							</Link>
						</Text>
					)}
					{hasUnmappedRequiredFields && (
						<SectionMessage title={messages.requiredFieldsSectionMessageTitle.defaultMessage}>
							<Text>
								{fg('confluence-issue-terminology-refresh') ? (
									<FormattedMessage
										{...messages.requiredFieldsSectionMessageDescriptionIssueTermRefresh}
									/>
								) : (
									<FormattedMessage {...messages.requiredFieldsSectionMessageDescription} />
								)}
							</Text>
						</SectionMessage>
					)}
					{(isCreatedFromTable ||
						(isCreatedFromActionItems && fg('confluence-create-work-items-from-action-items'))) &&
						bulkIssuesList.length > 50 && (
							<SectionMessage
								title={messages.moreThanFiftyWorkItemsSectionMessageTitle.defaultMessage}
							>
								<Text>
									<FormattedMessage {...messages.moreThanFiftyWorkItemsSectionMessageDescription} />
								</Text>
							</SectionMessage>
						)}
				</Stack>
				<Stack space="space.100">
					{bulkIssuesList.map((issue) => (
						<BulkCreateIssueCard key={issue.id} issue={issue} issueType={issueType} />
					))}
				</Stack>
			</Stack>
		</Box>
	);
};
